import { useMemo, useState } from "react";

import type { I_Country } from "../../shared/types.ts";

interface I_CountryFilter {
    available: I_Country[]
    selected: string[]
    onChange: (codes: string[]) => void
}

const PRESETS: Record<string, string[]> = {
    "DACH": ["DE", "AT", "CH"],
    "Nachbarländer": ["DE", "AT", "CH", "FR", "NL", "BE", "LU", "DK", "PL", "CZ"]
};

const CountryFilter = ({ available, selected, onChange }: I_CountryFilter) => {
    const [open, setOpen] = useState(false);
    const [filter, setFilter] = useState("");

    const visible = useMemo(() => {
        const needle = filter.trim().toLowerCase();

        if (!needle) return available;

        return available.filter((country) => country.name.toLowerCase().includes(needle) || country.code.toLowerCase().startsWith(needle));
    }, [available, filter]);

    const toggle = (code: string) => {
        onChange(selected.includes(code) ? selected.filter((entry) => entry !== code) : [...selected, code]);
    };

    // Only offer a preset where the data actually holds those countries
    const applyPreset = (codes: string[]) => {
        onChange(codes.filter((code) => available.some((country) => country.code === code)));
    };

    const label = selected.length === 0 ? "Alle Länder" : `${selected.length} ${selected.length === 1 ? "Land" : "Länder"}`;

    return (
        <div className="filter">
            <label>Länderfilter</label>

            <button className="filter-toggle" onClick={() => setOpen(!open)}>
                <span>{label}</span>
                <span className="filter-caret">{open ? "▲" : "▼"}</span>
            </button>

            {selected.length > 0 && (
                <div className="chips">
                    {selected.map((code) => (
                        <button
                            key={code}
                            className="chip"
                            title="Entfernen, auch mit der mittleren Maustaste"
                            onClick={() => toggle(code)}
                            onAuxClick={(event) => event.button === 1 && toggle(code)}
                            onMouseDown={(event) => event.button === 1 && event.preventDefault()} // Sonst startet der Autoscroll
                        >
                            {code} <span>×</span>
                        </button>
                    ))}
                </div>
            )}

            {open && (
                <div className="filter-panel">
                    <div className="filter-presets">
                        <button onClick={() => onChange([])}>Alle</button>
                        {Object.entries(PRESETS).map(([name, codes]) => (
                            <button key={name} onClick={() => applyPreset(codes)}>{name}</button>
                        ))}
                    </div>

                    <input
                        type="text"
                        placeholder="Land suchen"
                        value={filter}
                        onChange={(event) => setFilter(event.target.value)}
                    />

                    <ul className="filter-list">
                        {visible.map((country) => (
                            <li key={country.code}>
                                <label>
                                    <input
                                        type="checkbox"
                                        checked={selected.includes(country.code)}
                                        onChange={() => toggle(country.code)}
                                    />
                                    <span className="filter-name">{country.name}</span>
                                    <span className="filter-count">{country.count.toLocaleString("de-DE")}</span>
                                </label>
                            </li>
                        ))}
                    </ul>
                </div>
            )}
        </div>
    )
}
export default CountryFilter;